import { CSSProperties, MutableRefObject, useEffect, useRef, useState } from 'react';
import { Autocomplete, CircularProgress, FormControl, TextField } from '@mui/material';
import { KeyValues } from '../models/Items/FilterOptions';
import { FIELDS_WITH_DISPLAY_TEXT, validations } from '../pages/Products/EditItem/Constants';
import { seriesOptionHasLeagoCode, seriesOptionLabel } from './AddSeriesNameModal';
import { getInputTitle } from '../utils/helpers';

type FunctionArguments = {
    fieldName: string;
    label: string;
    formik: any;
    setSelectedDropDownOptions: (value) => void;
    options: KeyValues[];
    style: CSSProperties;
    selectedDropDownOptions: any;
    disabled: boolean;
    stickyFields?: { fieldName: string, label: string }[];
    onNewValueBlur?: (value: string, force?: boolean) => void;
    onInputChange?: (value: string, reason?: string) => void;
    loading?: boolean;
    suspendCommit?: boolean;
    lockedSeriesRef?: MutableRefObject<{ id: number; name: string } | null>;
};

export default function AddOrSelectAutocomplete(props: FunctionArguments) {
    const { fieldName, label, formik, setSelectedDropDownOptions, options, style, selectedDropDownOptions, disabled, stickyFields, onNewValueBlur, onInputChange, loading, suspendCommit, lockedSeriesRef } = props;
    const typedByUserRef = useRef(false);
    const changeCommittedRef = useRef(false);
    const pendingCreateNameRef = useRef("");
    const safeOptions = Array.isArray(options) ? options : [];
    const selectedSeries = selectedDropDownOptions?.[fieldName];
    const [filterText, setFilterText] = useState(() => seriesOptionLabel(selectedSeries));

    useEffect(() => {
        if (!typedByUserRef.current) {
            setFilterText(seriesOptionLabel(selectedSeries));
        }
    }, [selectedSeries?.id, selectedSeries?.name]);

    const commitSelection = (value) => {
        formik.setFieldValue(fieldName, value?.id);
        if (FIELDS_WITH_DISPLAY_TEXT.hasOwnProperty(fieldName)) {
            formik.setFieldValue(FIELDS_WITH_DISPLAY_TEXT[fieldName], value?.name);
        }
        setSelectedDropDownOptions((current) => ({
            ...(current || {}),
            [fieldName]: value,
        }));
    };

    const lockedSeriesFor = (typedValue: string) => {
        const lockedSeries = lockedSeriesRef?.current;
        if (!lockedSeries?.id || !typedValue?.trim()) {
            return null;
        }
        return lockedSeries.name?.trim().toLowerCase() === typedValue.trim().toLowerCase()
            ? lockedSeries
            : null;
    };
    const seriesAutocompleteKey = selectedSeries?.id
        ? `${fieldName}-${selectedSeries.id}`
        : selectedSeries?.name
            ? `${fieldName}-pending`
            : `${fieldName}-empty`;

    return (
        <FormControl key={`${fieldName}-add-or-create`} className='add-or-select-autocomplete'>
            {!stickyFields?.find(field => field.fieldName === fieldName) && <label
                htmlFor={`outlined-${fieldName}`}
                style={{ textTransform: "capitalize" }}
            >
                {label}
            </label>}
            <Autocomplete
                key={seriesAutocompleteKey}
                id={`outlined-${fieldName}`}
                freeSolo
                options={loading ? [] : safeOptions}
                loading={loading}
                onInputChange={(event, value, reason) => {
                    if (reason === "input") {
                        typedByUserRef.current = true;
                        changeCommittedRef.current = false;
                    } else if (reason === "clear") {
                        typedByUserRef.current = false;
                    }
                    if (reason === "input" || reason === "clear" || reason === "reset") {
                        setFilterText(value ?? "");
                        onInputChange?.(value ?? "", reason);
                    }
                }}
                getOptionLabel={(option) => (typeof option === 'string' ? option : seriesOptionLabel(option))}
                renderOption={(props, option) => {
                    const labelText = seriesOptionLabel(option);
                    const isCreate = option?.id == null;
                    const optionText = isCreate ? `Add "${labelText}"` : labelText;
                    return (
                        <li
                            {...props}
                            key={isCreate ? `create-${labelText}` : option.id}
                            title={optionText}
                            onMouseDown={(event) => {
                                if (isCreate) {
                                    event.preventDefault();
                                    pendingCreateNameRef.current = labelText.trim();
                                }
                            }}
                            onClick={(event) => {
                                if (!isCreate) {
                                    props.onClick?.(event);
                                    return;
                                }
                                const name = labelText.trim();
                                pendingCreateNameRef.current = name;
                                props.onClick?.(event);
                                pendingCreateNameRef.current = "";
                                commitSelection({ name, id: null });
                                onNewValueBlur?.(name, true);
                            }}
                        >
                            {optionText}
                        </li>
                    );
                }}
                isOptionEqualToValue={(option, value) => String(option?.id) === String(value?.id)}
                value={
                    selectedDropDownOptions[fieldName]
                        ? selectedDropDownOptions[fieldName]
                        : null
                }
                style={style}
                filterOptions={(options, params) => {
                    const availableOptions = Array.isArray(options) ? options : [];
                    // Opening the list clears the filter when the text already matches the selected value.
                    const inputValue = (params.inputValue.trim() || filterText.trim());
                    const inputValueLower = inputValue.toLowerCase();

                    const filtered = availableOptions.filter((option) =>
                        seriesOptionLabel(option).toLowerCase().includes(inputValueLower)
                    );

                    if (inputValue !== "") {
                        const labelKey = availableOptions.some((option) => option?.name) || availableOptions.length === 0
                            ? "name"
                            : "description";
                        filtered.push({ [labelKey]: inputValue, id: null });
                    }

                    return filtered;
                }}
                disabled={disabled}
                onChange={(event, newValue) => {
                    changeCommittedRef.current = true;
                    const trimmedText = typeof newValue === "string" ? newValue.trim() : "";
                    if (!newValue || (typeof newValue === "string" && !trimmedText) || (typeof newValue !== "string" && !newValue?.toString().trim())) {
                        formik.setFieldValue(fieldName, undefined);
                        if (FIELDS_WITH_DISPLAY_TEXT.hasOwnProperty(fieldName)) {
                            formik.setFieldValue(FIELDS_WITH_DISPLAY_TEXT[fieldName], undefined);
                        }
                        setSelectedDropDownOptions({
                            ...selectedDropDownOptions,
                            [fieldName]: undefined,
                        });
                    } else {
                        let value;
                        if (typeof newValue === 'string') {
                            value = { name: trimmedText };
                        } else if (newValue && !newValue.id) {
                            // new custom option
                            value = { name: (newValue.name || newValue.description || "").trim(), id: null };
                        } else {
                            // existing option
                            value = newValue;
                        }
                        const lockedSeries = lockedSeriesFor(value?.name || value?.description || "");
                        const selection = lockedSeries || value;
                        if (selection?.id) {
                            typedByUserRef.current = false;
                            onInputChange?.(seriesOptionLabel(selection), "select");
                        }
                        commitSelection(selection);
                        if (!disabled && !selection?.id && (selection?.name || selection?.description) && onNewValueBlur) {
                            pendingCreateNameRef.current = "";
                            onNewValueBlur((selection.name || selection.description).trim(), true);
                        }
                    }
                }}
                onBlur={(e: any) => {
                    const pendingCreateName = pendingCreateNameRef.current;
                    pendingCreateNameRef.current = "";
                    if (pendingCreateName) {
                        commitSelection({ name: pendingCreateName, id: null });
                        onNewValueBlur?.(pendingCreateName, true);
                        return;
                    }
                    if (suspendCommit) {
                        return;
                    }
                    if (validations.fields[fieldName]) {
                        formik.validateField(fieldName);
                    }
                    formik.handleBlur(e);
                    if (changeCommittedRef.current) {
                        changeCommittedRef.current = false;
                        typedByUserRef.current = false;
                        return;
                    }
                    const typedValue = e.target?.value?.trim() || "";
                    const lockedSeries = lockedSeriesFor(typedValue);
                    const currentSelection = selectedDropDownOptions?.[fieldName];
                    const currentLabel = seriesOptionLabel(currentSelection);
                    let valueToBeSet;
                    let keepingNewSeries = false;
                    if (lockedSeries) {
                        valueToBeSet = lockedSeries;
                    } else if (
                        !typedByUserRef.current &&
                        currentSelection?.id &&
                        currentLabel.toLowerCase() === typedValue.toLowerCase()
                    ) {
                        valueToBeSet = currentSelection;
                    } else if (typedValue) {
                        keepingNewSeries = !currentSelection?.id
                            && currentLabel.toLowerCase() === typedValue.toLowerCase();
                        if (keepingNewSeries) {
                            valueToBeSet = { name: typedValue, id: null };
                        } else {
                            const seriesWithLeago = safeOptions.find((option) =>
                                seriesOptionHasLeagoCode(option) &&
                                seriesOptionLabel(option).toLowerCase() === typedValue.toLowerCase()
                            );
                            valueToBeSet = seriesWithLeago || { name: typedValue, id: null };
                        }
                    } else {
                        valueToBeSet = undefined;
                    }
                    typedByUserRef.current = false;
                    commitSelection(valueToBeSet);
                    if (!disabled && !valueToBeSet?.id && valueToBeSet?.name && onNewValueBlur && !keepingNewSeries) {
                        onNewValueBlur(valueToBeSet.name);
                    }
                }}
                renderInput={(params) => <TextField
                    {...params}
                    className="add-or-select-input"
                    error={Boolean(formik.errors[fieldName])}
                    label={formik.errors[fieldName] ? formik.errors[fieldName] : (stickyFields?.find(field => field.fieldName === fieldName) ? label : "")}
                    key={params.id}
                    inputProps={{
                        ...params.inputProps,
                        title: getInputTitle(params.inputProps?.value),
                    }}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {loading ? <CircularProgress color="inherit" size={18} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
                }
            />
        </FormControl>
    );
}
